import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { BuddyRead, BuddyReadMember } from "@/types/buddyReads";
import { useToast } from "@/hooks/use-toast";

export function useBuddyReads() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [buddyReads, setBuddyReads] = useState<BuddyRead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    fetchBuddyReads();
    setupRealtimeSubscription();
  }, [user]);

  const fetchBuddyReads = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch buddy reads where user is a member
      const { data: memberData, error: memberError } = await supabase
        .from('buddy_read_members')
        .select('buddy_read_id')
        .eq('user_id', user.id)
        .eq('status', 'accepted');

      if (memberError) throw memberError;

      const buddyReadIds = memberData?.map(m => m.buddy_read_id) || [];

      if (buddyReadIds.length === 0) {
        setBuddyReads([]);
        setLoading(false);
        return;
      }

      // Fetch buddy reads with book and member details
      const { data: buddyReadsData, error: readsError } = await supabase
        .from('buddy_reads')
        .select(`
          *,
          book:books(id, title, authors, cover_url, page_count)
        `)
        .in('id', buddyReadIds)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (readsError) throw readsError;

      // Fetch all members for these buddy reads
      const { data: membersData, error: membersError } = await supabase
        .from('buddy_read_members')
        .select(`
          *,
          user:profiles(id, username, name, image)
        `)
        .in('buddy_read_id', buddyReadIds)
        .eq('status', 'accepted');

      if (membersError) throw membersError;

      // Combine the data
      const enrichedBuddyReads = buddyReadsData?.map(br => ({
        ...br,
        status: br.status as 'active' | 'completed' | 'abandoned',
        book: Array.isArray(br.book) ? br.book[0] : br.book,
        members: membersData?.filter(m => m.buddy_read_id === br.id).map(m => ({
          ...m,
          status: m.status as 'invited' | 'accepted' | 'declined',
          user: Array.isArray(m.user) ? m.user[0] : m.user
        })) || []
      }));

      setBuddyReads(enrichedBuddyReads || []);
    } catch (error: any) {
      console.error('Error fetching buddy reads:', error);
      toast({
        title: "Error loading buddy reads",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user) return;

    const channel = supabase
      .channel('buddy-reads-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'buddy_read_members',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          // New invitation received
          if (payload.new.status === 'invited') {
            // Fetch buddy read details
            const { data: buddyReadData } = await supabase
              .from('buddy_reads')
              .select(`
                *,
                book:books(title),
                creator:profiles!buddy_reads_created_by_fkey(name, username)
              `)
              .eq('id', payload.new.buddy_read_id)
              .single();

            if (buddyReadData) {
              const creator = Array.isArray(buddyReadData.creator) 
                ? buddyReadData.creator[0] 
                : buddyReadData.creator;
              const book = Array.isArray(buddyReadData.book) 
                ? buddyReadData.book[0] 
                : buddyReadData.book;

              toast({
                title: "New Buddy Read Invitation!",
                description: `${creator?.name || creator?.username} invited you to read "${book?.title}"`,
              });
            }
          }
          fetchBuddyReads();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'buddy_read_members'
        },
        async (payload) => {
          // Check if it's a progress update from another member in user's buddy reads
          const { data: myMemberships } = await supabase
            .from('buddy_read_members')
            .select('buddy_read_id')
            .eq('user_id', user.id)
            .eq('status', 'accepted');

          const myBuddyReadIds = myMemberships?.map(m => m.buddy_read_id) || [];

          if (
            payload.new.user_id !== user.id &&
            myBuddyReadIds.includes(payload.new.buddy_read_id) &&
            payload.old.current_page !== payload.new.current_page
          ) {
            // Another member updated their progress
            const { data: memberData } = await supabase
              .from('profiles')
              .select('name, username')
              .eq('id', payload.new.user_id)
              .single();

            const { data: buddyReadData } = await supabase
              .from('buddy_reads')
              .select(`
                *,
                book:books(title, page_count)
              `)
              .eq('id', payload.new.buddy_read_id)
              .single();

            if (memberData && buddyReadData) {
              const book = Array.isArray(buddyReadData.book) 
                ? buddyReadData.book[0] 
                : buddyReadData.book;
              const percentage = book?.page_count 
                ? Math.round((payload.new.current_page / book.page_count) * 100)
                : 0;

              toast({
                title: "Buddy Read Progress Update",
                description: `${memberData.name || memberData.username} is ${percentage}% through "${book?.title}"`,
              });
            }
          }
          fetchBuddyReads();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'buddy_reads'
        },
        () => {
          fetchBuddyReads();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const createBuddyRead = async (input: {
    book_id: string;
    title?: string;
    start_date?: string;
    target_end_date?: string;
    member_ids: string[];
  }) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      // Create buddy read
      const { data: buddyRead, error: createError } = await supabase
        .from('buddy_reads')
        .insert({
          created_by: user.id,
          book_id: input.book_id,
          title: input.title,
          start_date: input.start_date,
          target_end_date: input.target_end_date,
          status: 'active'
        })
        .select()
        .single();

      if (createError) throw createError;

      // Add creator as accepted member
      const membersToAdd = [
        {
          buddy_read_id: buddyRead.id,
          user_id: user.id,
          status: 'accepted',
          current_page: 0
        },
        ...input.member_ids.map(member_id => ({
          buddy_read_id: buddyRead.id,
          user_id: member_id,
          status: 'invited',
          current_page: 0
        }))
      ];

      const { error: membersError } = await supabase
        .from('buddy_read_members')
        .insert(membersToAdd);

      if (membersError) throw membersError;

      toast({
        title: "Buddy read created!",
        description: "Your friends have been invited to join."
      });

      await fetchBuddyReads();
      return { data: buddyRead, error: null };
    } catch (error: any) {
      console.error('Error creating buddy read:', error);
      toast({
        title: "Error creating buddy read",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }
  };

  const updateProgress = async (buddyReadId: string, currentPage: number) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('buddy_read_members')
        .update({
          current_page: currentPage,
          last_updated: new Date().toISOString()
        })
        .eq('buddy_read_id', buddyReadId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Progress updated!",
        description: `You're on page ${currentPage}`
      });
    } catch (error: any) {
      console.error('Error updating progress:', error);
      toast({
        title: "Error updating progress",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const acceptInvite = async (buddyReadId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('buddy_read_members')
        .update({ status: 'accepted' })
        .eq('buddy_read_id', buddyReadId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Invitation accepted!",
        description: "You've joined the buddy read"
      });

      await fetchBuddyReads();
    } catch (error: any) {
      console.error('Error accepting invite:', error);
      toast({
        title: "Error accepting invite",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  return {
    buddyReads,
    loading,
    createBuddyRead,
    updateProgress,
    acceptInvite,
    refetch: fetchBuddyReads
  };
}
